import type {InfiniteData, QueryKey} from '@tanstack/react-query';
import {produce} from 'immer';
import queryClient from '@/utils/reactQueryClient';
import type {ApiRecruitmentsResponse} from '@/views/creators/recruit-crm/types';
import {getRecruitmentsQueryKeyForAgency} from '@/hooks/queries/use-recruitments-query';

/** Parameters for optimistically removing a recruitment from all cached query results. */
export interface RemoveRecruitmentFromCacheParams {
  agencyId: string;
  recruitmentId: string;
}

/** Parameters for optimistically removing multiple recruitments from all cached query results. */
export interface BulkRemoveRecruitmentsFromCacheParams {
  agencyId: string;
  recruitmentIds: string[];
}

/** A snapshot of a single query cache entry, used to roll back an optimistic update on error. */
export interface RecruitmentCacheSnapshot {
  queryKey: QueryKey;
  previousData: InfiniteData<ApiRecruitmentsResponse>;
}

/**
 * Optimistically remove a recruitment from all recruitments query caches for the given agency.
 * Returns snapshots so the caller can restore on mutation error.
 */
export function removeRecruitmentFromRecruitmentsCache({
  agencyId,
  recruitmentId,
}: RemoveRecruitmentFromCacheParams): RecruitmentCacheSnapshot[] {
  const snapshots: RecruitmentCacheSnapshot[] = [];

  const entries = queryClient.getQueriesData<
    InfiniteData<ApiRecruitmentsResponse>
  >({
    queryKey: getRecruitmentsQueryKeyForAgency(agencyId),
  });

  entries.forEach(([queryKey, data]) => {
    if (data == null) {
      return;
    }

    snapshots.push({queryKey, previousData: data});

    // Optimistically update the cache: remove the recruitment from every loaded
    // page and decrement the total count so the UI reflects the deletion
    // immediately, before the API responds.
    queryClient.setQueryData<InfiniteData<ApiRecruitmentsResponse>>(
      queryKey,
      (old) => {
        if (!old) {
          return old;
        }

        return produce(old, (draft) => {
          // Strip the deleted recruitment from each page's list.
          draft.pages.forEach((page) => {
            page.recruitments = page.recruitments.filter(
              (r) => r.id !== recruitmentId
            );
          });

          // pages[0].meta holds the authoritative total shown in the UI counter.
          if (draft.pages[0]) {
            draft.pages[0].meta.totalCount = Math.max(
              0,
              draft.pages[0].meta.totalCount - 1
            );
          }
        });
      }
    );
  });

  return snapshots;
}

/**
 * Optimistically remove multiple recruitments from all recruitments query caches for the given agency.
 * Returns snapshots so the caller can restore on mutation error.
 */
export function bulkRemoveRecruitmentsFromCache({
  agencyId,
  recruitmentIds,
}: BulkRemoveRecruitmentsFromCacheParams): RecruitmentCacheSnapshot[] {
  const snapshots: RecruitmentCacheSnapshot[] = [];
  const idsToRemove = new Set(recruitmentIds);

  const entries = queryClient.getQueriesData<
    InfiniteData<ApiRecruitmentsResponse>
  >({
    queryKey: getRecruitmentsQueryKeyForAgency(agencyId),
  });

  entries.forEach(([queryKey, data]) => {
    if (data == null) {
      return;
    }

    snapshots.push({queryKey, previousData: data});

    queryClient.setQueryData<InfiniteData<ApiRecruitmentsResponse>>(
      queryKey,
      (old) => {
        if (!old) {
          return old;
        }

        return produce(old, (draft) => {
          let removedCount = 0;
          draft.pages.forEach((page) => {
            const before = page.recruitments.length;
            page.recruitments = page.recruitments.filter(
              (r) => !idsToRemove.has(r.id)
            );
            removedCount += before - page.recruitments.length;
          });

          if (draft.pages[0]) {
            draft.pages[0].meta.totalCount = Math.max(
              0,
              draft.pages[0].meta.totalCount - removedCount
            );
          }
        });
      }
    );
  });

  return snapshots;
}
